<template>
  <div class="open-api-doc-page">
    <div class="doc-panel">
      <header class="page-heading">
        <div>
          <div class="eyebrow">OPEN API DOCUMENTATION</div>
          <h1>开放接口文档</h1>
          <p>接口目录实时同步 Springdoc，可为每个 operationId 维护说明、注意事项及请求响应示例。</p>
        </div>
        <div class="heading-actions">
          <el-button class="secondary-button" icon="el-icon-view" @click="openPortal">查看公开文档</el-button>
          <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">同步接口</el-button>
        </div>
      </header>

      <section class="summary-grid">
        <div class="summary-card"><span>接口总数</span><strong>{{ endpoints.length }}</strong></div>
        <div class="summary-card configured"><span>已配置文档</span><strong>{{ configuredCount }}</strong></div>
        <div class="summary-card pending"><span>待完善</span><strong>{{ endpoints.length - configuredCount }}</strong></div>
      </section>

      <div class="filter-panel">
        <el-input
          v-model.trim="query.keyword"
          clearable
          placeholder="搜索接口名称、路径或 operationId"
        />
        <el-select v-model="query.method" clearable placeholder="全部方法" popper-class="open-api-doc-select">
          <el-option v-for="method in methods" :key="method" :label="method" :value="method" />
        </el-select>
        <el-select v-model="query.status" clearable placeholder="全部配置状态" popper-class="open-api-doc-select">
          <el-option label="已配置" value="configured" />
          <el-option label="待完善" value="pending" />
        </el-select>
      </div>

      <div class="table-panel">
        <el-table
          v-loading="loading"
          class="doc-table"
          :data="filteredEndpoints"
          border
          height="100%"
          empty-text="暂无匹配的开放接口"
        >
          <el-table-column label="方法" width="88" align="center">
            <template slot-scope="scope"><span :class="['method-badge', scope.row.method.toLowerCase()]">{{ scope.row.method }}</span></template>
          </el-table-column>
          <el-table-column prop="summary" label="接口名称" min-width="170" show-overflow-tooltip />
          <el-table-column label="请求路径" min-width="260" show-overflow-tooltip>
            <template slot-scope="scope"><code class="path-code">{{ scope.row.path }}</code></template>
          </el-table-column>
          <el-table-column prop="operationId" label="operationId" min-width="210" show-overflow-tooltip />
          <el-table-column prop="groupName" label="分组" width="130" show-overflow-tooltip />
          <el-table-column label="文档状态" width="105" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.configured ? 'success' : 'info'" size="small">{{ scope.row.configured ? '已配置' : '待完善' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="更新时间" width="165" align="center">
            <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="175" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="doc-actions">
                <el-button type="text" :disabled="!scope.row.operationId" @click="openEdit(scope.row)">编辑文档</el-button>
                <el-button type="text" :disabled="!scope.row.operationId" @click="openEndpoint(scope.row)">预览</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-dialog
      :visible.sync="editorVisible"
      width="1240px"
      :fullscreen="editorFullscreen"
      custom-class="open-api-doc-dialog"
      append-to-body
      :close-on-click-modal="false"
      :before-close="handleEditorClose"
    >
      <div slot="title" class="editor-dialog-title">
        <span class="dialog-title-icon"><i class="el-icon-edit-outline"></i></span>
        <div>
          <strong>编辑接口文档</strong>
          <small>编辑内容保存后将同步展示在开放平台</small>
        </div>
      </div>

      <div class="endpoint-identity">
        <div class="identity-copy">
          <strong>{{ activeEndpoint.summary }}</strong>
        </div>
        <div class="identity-route">
          <span :class="['method-badge', activeEndpoint.method.toLowerCase()]">{{ activeEndpoint.method }}</span>
          <code>{{ activeEndpoint.path }}</code>
        </div>
        <el-button
          class="editor-size-button"
          type="text"
          :icon="editorFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"
          @click="editorFullscreen = !editorFullscreen"
        >{{ editorFullscreen ? '退出全屏' : '全屏编辑' }}</el-button>
      </div>

      <div class="editor-workspace">
        <el-form class="editor-column" label-position="top">
          <el-form-item class="editor-section">
            <template slot="label">
              <div class="field-heading">
                <div><span class="field-order">01</span><strong>接口说明</strong></div>
                <small>Markdown · 最多 20,000 字</small>
              </div>
            </template>
            <div class="markdown-editor-shell">
              <div class="markdown-toolbar">
                <div class="format-actions">
                  <el-button size="mini" title="二级标题" @click="insertMarkdown('description', 'heading')">H2</el-button>
                  <el-button size="mini" title="粗体" @click="insertMarkdown('description', 'bold')"><strong>B</strong></el-button>
                  <el-button size="mini" title="斜体" @click="insertMarkdown('description', 'italic')"><em>I</em></el-button>
                  <el-button size="mini" title="删除线" @click="insertMarkdown('description', 'strike')"><s>S</s></el-button>
                  <el-button size="mini" title="行内代码" @click="insertMarkdown('description', 'code')"><i class="el-icon-coordinate"></i></el-button>
                  <el-button size="mini" title="无序列表" @click="insertMarkdown('description', 'unordered')"><i class="el-icon-s-unfold"></i></el-button>
                  <el-button size="mini" title="有序列表" @click="insertMarkdown('description', 'ordered')">1.</el-button>
                  <el-button size="mini" title="任务列表" @click="insertMarkdown('description', 'task')"><i class="el-icon-finished"></i></el-button>
                  <el-button size="mini" title="引用" @click="insertMarkdown('description', 'quote')">“</el-button>
                  <el-button size="mini" title="代码块" @click="insertMarkdown('description', 'codeBlock')">&lt;/&gt;</el-button>
                  <el-button size="mini" title="链接" @click="insertMarkdown('description', 'link')"><i class="el-icon-link"></i></el-button>
                  <el-button size="mini" title="图片" @click="insertMarkdown('description', 'image')"><i class="el-icon-picture-outline"></i></el-button>
                  <el-button size="mini" title="表格" @click="insertMarkdown('description', 'table')"><i class="el-icon-s-grid"></i></el-button>
                  <el-button size="mini" title="分割线" @click="insertMarkdown('description', 'horizontal')">—</el-button>
                </div>
                <el-button class="template-button" size="mini" icon="el-icon-document-add" @click="insertTemplate('description')">说明模板</el-button>
              </div>
              <el-input
                ref="descriptionEditor"
                v-model="form.description"
                type="textarea"
                :rows="12"
                maxlength="20000"
                show-word-limit
                placeholder="说明接口用途、适用场景及调用结果。右侧会实时展示发布效果。"
              />
            </div>
          </el-form-item>
          <el-form-item class="editor-section">
            <template slot="label">
              <div class="field-heading">
                <div><span class="field-order">02</span><strong>注意事项</strong></div>
                <small>Markdown · 最多 10,000 字</small>
              </div>
            </template>
            <div class="markdown-editor-shell">
              <div class="markdown-toolbar">
                <div class="format-actions">
                  <el-button size="mini" title="二级标题" @click="insertMarkdown('notes', 'heading')">H2</el-button>
                  <el-button size="mini" title="粗体" @click="insertMarkdown('notes', 'bold')"><strong>B</strong></el-button>
                  <el-button size="mini" title="斜体" @click="insertMarkdown('notes', 'italic')"><em>I</em></el-button>
                  <el-button size="mini" title="删除线" @click="insertMarkdown('notes', 'strike')"><s>S</s></el-button>
                  <el-button size="mini" title="行内代码" @click="insertMarkdown('notes', 'code')"><i class="el-icon-coordinate"></i></el-button>
                  <el-button size="mini" title="无序列表" @click="insertMarkdown('notes', 'unordered')"><i class="el-icon-s-unfold"></i></el-button>
                  <el-button size="mini" title="有序列表" @click="insertMarkdown('notes', 'ordered')">1.</el-button>
                  <el-button size="mini" title="任务列表" @click="insertMarkdown('notes', 'task')"><i class="el-icon-finished"></i></el-button>
                  <el-button size="mini" title="引用" @click="insertMarkdown('notes', 'quote')">“</el-button>
                  <el-button size="mini" title="代码块" @click="insertMarkdown('notes', 'codeBlock')">&lt;/&gt;</el-button>
                  <el-button size="mini" title="链接" @click="insertMarkdown('notes', 'link')"><i class="el-icon-link"></i></el-button>
                  <el-button size="mini" title="图片" @click="insertMarkdown('notes', 'image')"><i class="el-icon-picture-outline"></i></el-button>
                  <el-button size="mini" title="表格" @click="insertMarkdown('notes', 'table')"><i class="el-icon-s-grid"></i></el-button>
                  <el-button size="mini" title="分割线" @click="insertMarkdown('notes', 'horizontal')">—</el-button>
                </div>
                <el-button class="template-button" size="mini" icon="el-icon-document-add" @click="insertTemplate('notes')">注意事项模板</el-button>
              </div>
              <el-input
                ref="notesEditor"
                v-model="form.notes"
                type="textarea"
                :rows="8"
                maxlength="10000"
                show-word-limit
                placeholder="填写权限、限流、幂等或数据范围等调用约束。"
              />
            </div>
          </el-form-item>
          <el-form-item class="editor-section example-editor-section">
            <template slot="label">
              <div class="field-heading">
                <div><span class="field-order">03</span><strong>请求示例</strong></div>
                <el-button class="add-example-button" size="mini" icon="el-icon-plus" @click="addExample('requestExamples')">添加示例</el-button>
              </div>
            </template>
            <div v-if="form.requestExamples.length" class="example-editor-list">
              <div v-for="(example, index) in form.requestExamples" :key="example.localKey" class="example-editor-card">
                <div class="example-card-heading">
                  <strong>请求示例 {{ index + 1 }}</strong>
                  <el-button type="text" icon="el-icon-delete" @click="removeExample('requestExamples', index)">删除</el-button>
                </div>
                <div class="example-meta-grid">
                  <el-input v-model="example.name" maxlength="100" placeholder="示例名称（可选）" />
                  <el-input v-model="example.contentType" maxlength="100" placeholder="Content-Type，如 application/json" />
                </div>
                <el-input v-model="example.content" type="textarea" :rows="8" maxlength="50000" show-word-limit placeholder="输入完整的请求示例内容" />
                <el-input v-model="example.description" maxlength="1000" placeholder="示例说明（可选）" />
              </div>
            </div>
            <div v-else class="example-editor-empty">暂无请求示例，点击“添加示例”开始编辑。</div>
          </el-form-item>
          <el-form-item class="editor-section example-editor-section">
            <template slot="label">
              <div class="field-heading">
                <div><span class="field-order">04</span><strong>响应示例</strong></div>
                <el-button class="add-example-button" size="mini" icon="el-icon-plus" @click="addExample('responseExamples')">添加示例</el-button>
              </div>
            </template>
            <div v-if="form.responseExamples.length" class="example-editor-list">
              <div v-for="(example, index) in form.responseExamples" :key="example.localKey" class="example-editor-card">
                <div class="example-card-heading">
                  <strong>响应示例 {{ index + 1 }}</strong>
                  <el-button type="text" icon="el-icon-delete" @click="removeExample('responseExamples', index)">删除</el-button>
                </div>
                <div class="example-meta-grid">
                  <el-input v-model="example.name" maxlength="100" placeholder="示例名称（可选）" />
                  <el-input v-model="example.contentType" maxlength="100" placeholder="Content-Type，如 application/json" />
                </div>
                <el-input v-model="example.content" type="textarea" :rows="8" maxlength="50000" show-word-limit placeholder="输入完整的响应示例内容" />
                <el-input v-model="example.description" maxlength="1000" placeholder="示例说明（可选）" />
              </div>
            </div>
            <div v-else class="example-editor-empty">暂无响应示例，点击“添加示例”开始编辑。</div>
          </el-form-item>
          <div class="markdown-help">
            <i class="el-icon-info"></i>
            支持表格、任务列表、删除线、图片、自动链接、嵌套列表和围栏代码块。快捷键：Ctrl + S 保存。
          </div>
        </el-form>

        <aside ref="livePreview" :class="['live-preview', { 'has-scrollbar': previewHasScrollbar }]">
          <div class="live-preview-panel">
            <div class="preview-heading">
              <div><span class="live-dot"></span><strong>实时预览</strong></div>
              <small>与公开文档渲染效果一致</small>
            </div>
            <div class="preview-scroll">
            <section>
              <h3>接口说明</h3>
              <div v-if="form.description" class="markdown-body" v-html="descriptionHtml"></div>
              <p v-else class="empty-preview">输入接口说明后将在这里实时显示</p>
            </section>
            <section>
              <h3>注意事项</h3>
              <div v-if="form.notes" class="markdown-body notes-body" v-html="notesHtml"></div>
              <p v-else class="empty-preview">暂无注意事项</p>
            </section>
            <section>
              <h3>请求示例</h3>
              <div v-if="form.requestExamples.length" class="example-preview-list">
                <div v-for="(example, index) in form.requestExamples" :key="example.localKey" class="example-preview-card">
                  <div><strong>{{ example.name || `请求示例 ${index + 1}` }}</strong><code>{{ example.contentType || 'application/json' }}</code></div>
                  <pre><code>{{ formatExampleContent(example) }}</code></pre>
                  <p v-if="example.description">{{ example.description }}</p>
                </div>
              </div>
              <p v-else class="empty-preview">暂无请求示例</p>
            </section>
            <section>
              <h3>响应示例</h3>
              <div v-if="form.responseExamples.length" class="example-preview-list">
                <div v-for="(example, index) in form.responseExamples" :key="example.localKey" class="example-preview-card">
                  <div><strong>{{ example.name || `响应示例 ${index + 1}` }}</strong><code>{{ example.contentType || 'application/json' }}</code></div>
                  <pre><code>{{ formatExampleContent(example) }}</code></pre>
                  <p v-if="example.description">{{ example.description }}</p>
                </div>
              </div>
              <p v-else class="empty-preview">暂无响应示例</p>
            </section>
            </div>
          </div>
        </aside>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button v-if="form.id" type="text" class="delete-button" @click="removeDocument">清除文档</el-button>
        <span class="footer-spacer"></span>
        <span class="save-shortcut">Ctrl + S 保存</span>
        <el-button class="secondary-button" @click="requestEditorClose">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveDocument">保存并发布</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { renderMarkdown } from '../../utils/markdown';
import { formatExampleContent } from '../../utils/openApiExample';

const emptyEndpoint = () => ({ method: '', path: '', summary: '', operationId: '', description: '', notes: '', requestExamples: [], responseExamples: [] });

export default {
  name: 'OpenApiDocIndex',
  data() {
    return {
      loading: false,
      saving: false,
      editorVisible: false,
      editorFullscreen: false,
      editorSnapshot: '',
      previewHasScrollbar: false,
      nextExampleKey: 1,
      methods: [],
      endpoints: [],
      activeEndpoint: emptyEndpoint(),
      form: { id: null, operationId: '', description: '', notes: '', requestExamples: [], responseExamples: [] },
      query: { keyword: '', method: '', status: '' }
    };
  },
  computed: {
    configuredCount() {
      return this.endpoints.filter(item => item.configured).length;
    },
    filteredEndpoints() {
      const keyword = this.query.keyword.toLowerCase();
      return this.endpoints.filter(item => {
        const haystack = [item.summary, item.path, item.operationId, item.groupName].join(' ').toLowerCase();
        const matchesKeyword = !keyword || haystack.indexOf(keyword) !== -1;
        const matchesMethod = !this.query.method || item.method === this.query.method;
        const matchesStatus = !this.query.status
          || (this.query.status === 'configured' ? item.configured : !item.configured);
        return matchesKeyword && matchesMethod && matchesStatus;
      });
    },
    descriptionHtml() {
      return renderMarkdown(this.form.description);
    },
    notesHtml() {
      return renderMarkdown(this.form.notes);
    }
  },
  mounted() {
    window.addEventListener('resize', this.updatePreviewScrollbar);
    window.addEventListener('keydown', this.handleEditorKeydown, true);
    if (Number(localStorage.getItem('a-xny-level')) !== 1) {
      this.$message.warning('仅平台管理员可以访问开放接口文档管理');
      this.$router.replace({ name: 'indexVue' });
      return;
    }
    document.title = '开放接口文档';
    this.loadData();
  },
  updated() {
    this.updatePreviewScrollbar();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePreviewScrollbar);
    window.removeEventListener('keydown', this.handleEditorKeydown, true);
  },
  methods: {
    formatExampleContent(example) {
      if (!example || !example.content) return '等待输入示例内容';
      return formatExampleContent(example.content, example.contentType);
    },
    updatePreviewScrollbar() {
      const preview = this.$refs.livePreview;
      const hasScrollbar = Boolean(preview && this.editorVisible && preview.scrollHeight > preview.clientHeight + 1);
      if (hasScrollbar !== this.previewHasScrollbar) {
        this.previewHasScrollbar = hasScrollbar;
      }
    },
    loadData() {
      if (this.loading) return;
      this.loading = true;
      Promise.all([
        this.$request.get('/v3/api-docs/open'),
        this.$request.get('/api/open-api-doc/endpoint/list')
      ]).then(([openApiResponse, extensionResponse]) => {
        const document = openApiResponse && openApiResponse.data;
        const extensionResult = extensionResponse && extensionResponse.data;
        if (!document || !document.paths) throw new Error('Springdoc 返回格式不正确');
        if (!extensionResult || !extensionResult.result) {
          throw new Error((extensionResult && extensionResult.message) || '扩展文档加载失败');
        }
        this.endpoints = this.buildEndpoints(document, extensionResult.data || []);
        this.methods = Array.from(new Set(this.endpoints.map(item => item.method))).sort();
      }).catch(error => {
        this.$message.error(this.errorMessage(error, '开放接口目录加载失败'));
      }).finally(() => {
        this.loading = false;
      });
    },
    buildEndpoints(document, extensions) {
      const extensionMap = {};
      extensions.forEach(item => { extensionMap[item.operationId] = item; });
      const endpoints = [];
      const httpMethods = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];
      Object.keys(document.paths || {}).forEach(path => {
        const pathItem = document.paths[path] || {};
        httpMethods.forEach(method => {
          const operation = pathItem[method];
          if (!operation) return;
          const operationId = operation.operationId || '';
          const extension = extensionMap[operationId] || {};
          endpoints.push({
            method: method.toUpperCase(),
            path,
            operationId,
            summary: operation.summary || operationId || `${method.toUpperCase()} ${path}`,
            groupName: operation.tags && operation.tags.length ? operation.tags[0] : '未分组',
            id: extension.id || null,
            description: extension.description || '',
            notes: extension.notes || '',
            requestExamples: extension.requestExamples || [],
            responseExamples: extension.responseExamples || [],
            createTime: extension.createTime || '',
            updateTime: extension.updateTime || '',
            configured: Boolean(extension.id)
          });
        });
      });
      return endpoints.sort((left, right) => {
        const groupCompare = left.groupName.localeCompare(right.groupName, 'zh-CN');
        return groupCompare || left.path.localeCompare(right.path) || left.method.localeCompare(right.method);
      });
    },
    openEdit(row) {
      this.activeEndpoint = { ...row };
      this.form = {
        id: row.id,
        operationId: row.operationId,
        description: row.description || '',
        notes: row.notes || '',
        requestExamples: this.cloneExamples(row.requestExamples),
        responseExamples: this.cloneExamples(row.responseExamples)
      };
      this.editorFullscreen = false;
      this.editorSnapshot = this.documentSnapshot();
      this.editorVisible = true;
    },
    documentSnapshot() {
      return JSON.stringify({
        description: this.form.description || '',
        notes: this.form.notes || '',
        requestExamples: this.form.requestExamples,
        responseExamples: this.form.responseExamples
      });
    },
    cloneExamples(examples) {
      return (examples || []).map(example => ({
        id: example.id || null,
        localKey: this.nextExampleKey++,
        name: example.name || '',
        description: example.description || '',
        contentType: example.contentType || 'application/json',
        content: example.content || ''
      }));
    },
    addExample(field) {
      if (this.form[field].length >= 20) {
        this.$message.warning('每类最多添加 20 个示例');
        return;
      }
      this.form[field].push({
        id: null,
        localKey: this.nextExampleKey++,
        name: '',
        description: '',
        contentType: 'application/json',
        content: ''
      });
    },
    removeExample(field, index) {
      this.form[field].splice(index, 1);
    },
    requestEditorClose() {
      this.handleEditorClose(() => { this.editorVisible = false; });
    },
    handleEditorClose(done) {
      if (this.saving) return;
      if (this.documentSnapshot() === this.editorSnapshot) {
        done();
        return;
      }
      this.$confirm('当前修改尚未保存，确定放弃吗？', '放弃修改', { type: 'warning' })
        .then(done)
        .catch(() => {});
    },
    insertMarkdown(field, type) {
      const editorRef = field === 'description' ? 'descriptionEditor' : 'notesEditor';
      const editor = this.$refs[editorRef] && this.$refs[editorRef].$refs.textarea;
      const source = this.form[field] || '';
      const start = editor ? editor.selectionStart : source.length;
      const end = editor ? editor.selectionEnd : source.length;
      const selected = source.slice(start, end);
      const wrappers = {
        bold: ['**', '**', '重点内容'],
        italic: ['*', '*', '强调内容'],
        strike: ['~~', '~~', '已废弃内容'],
        code: ['`', '`', 'code'],
        link: ['[', '](https://example.com)', '链接文字'],
        image: ['![', '](https://example.com/image.png)', '图片说明']
      };
      const linePrefixes = {
        heading: () => '## ',
        unordered: () => '- ',
        ordered: index => `${index + 1}. `,
        task: () => '- [ ] ',
        quote: () => '> '
      };
      const blocks = {
        table: '| 字段 | 类型 | 说明 |\n| --- | --- | --- |\n| id | string | 字段说明 |',
        horizontal: '---',
        codeBlock: `\`\`\`json\n${selected || '{\n  "key": "value"\n}'}\n\`\`\``
      };
      let insertion = '';
      let replaceStart = start;
      let replaceEnd = end;
      let selectionOffset = 0;
      let selectionLength = 0;

      if (wrappers[type]) {
        const [prefix, suffix, fallback] = wrappers[type];
        const content = selected || fallback;
        insertion = prefix + content + suffix;
        selectionOffset = prefix.length;
        selectionLength = content.length;
      } else if (linePrefixes[type]) {
        replaceStart = source.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
        const nextLineBreak = source.indexOf('\n', end);
        replaceEnd = nextLineBreak === -1 ? source.length : nextLineBreak;
        const content = source.slice(replaceStart, replaceEnd) || (type === 'heading' ? '小节标题' : '列表项');
        insertion = content.split('\n').map((line, index) => linePrefixes[type](index) + line).join('\n');
        selectionOffset = linePrefixes[type](0).length;
        selectionLength = content.length;
      } else if (blocks[type]) {
        const before = source.slice(0, replaceStart);
        const after = source.slice(replaceEnd);
        const leadingBreak = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
        const trailingBreak = after && !after.startsWith('\n\n') ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
        insertion = leadingBreak + blocks[type] + trailingBreak;
        selectionOffset = leadingBreak.length + (type === 'codeBlock' ? 8 : 0);
        selectionLength = type === 'codeBlock'
          ? (selected || '{\n  "key": "value"\n}').length
          : blocks[type].length;
      } else {
        return;
      }

      this.form[field] = source.slice(0, replaceStart) + insertion + source.slice(replaceEnd);
      this.$nextTick(() => {
        if (!editor) return;
        editor.focus();
        const selectionStart = replaceStart + selectionOffset;
        editor.setSelectionRange(selectionStart, selectionStart + selectionLength);
      });
    },
    handleEditorKeydown(event) {
      if (!this.editorVisible || event.altKey || (!event.ctrlKey && !event.metaKey)) return;
      const key = String(event.key || '').toLowerCase();
      if (key === 's') {
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat || this.saving) return;
        this.saveDocument();
      }
    },
    insertTemplate(field) {
      const templates = {
        description: [
          '## 接口用途',
          '',
          '说明该接口解决的业务问题和适用场景。',
          '',
          '## 调用结果',
          '',
          '- 成功后产生的业务结果',
          '- 需要调用方保存或继续处理的数据'
        ].join('\n'),
        notes: [
          '## 调用约束',
          '',
          '- 调用前需要具备的权限',
          '- 数据范围或频率限制',
          '',
          '## 特别说明',
          '',
          '> 请补充幂等、重试或敏感数据处理要求。'
        ].join('\n')
      };
      if (this.form[field] && this.form[field].trim()) {
        this.$confirm('插入模板将替换当前区块内容，是否继续？', '插入文档模板', { type: 'warning' })
          .then(() => { this.form[field] = templates[field]; })
          .catch(() => {});
        return;
      }
      this.form[field] = templates[field];
    },
    saveDocument() {
      if (this.saving || !this.form.operationId) return;
      const examples = this.form.requestExamples.concat(this.form.responseExamples);
      if (!this.form.description.trim() && !this.form.notes.trim() && !examples.length) {
        this.$message.warning('接口说明、注意事项或请求响应示例至少填写一项；如需清空，请使用“清除文档”');
        return;
      }
      if (examples.some(example => !example.content || !example.content.trim())) {
        this.$message.warning('请填写所有请求响应示例的内容，或删除空示例');
        return;
      }
      this.saving = true;
      this.$request.post('/api/open-api-doc/endpoint/save', {
        operationId: this.form.operationId,
        description: this.form.description,
        notes: this.form.notes,
        requestExamples: this.form.requestExamples.map((example, index) => ({
          name: example.name,
          description: example.description,
          contentType: example.contentType,
          content: example.content,
          sort: index
        })),
        responseExamples: this.form.responseExamples.map((example, index) => ({
          name: example.name,
          description: example.description,
          contentType: example.contentType,
          content: example.content,
          sort: index
        }))
      }).then(response => {
        if (!response.data.result) {
          this.$message.error(response.data.message || '保存文档失败');
          return;
        }
        const saved = response.data.data || {};
        const target = this.endpoints.find(item => item.operationId === this.form.operationId);
        if (target) Object.assign(target, saved, { configured: true });
        this.editorSnapshot = this.documentSnapshot();
        this.editorVisible = false;
        this.$message.success('文档已保存并发布');
      }).catch(error => {
        this.$message.error(this.errorMessage(error, '保存文档失败'));
      }).finally(() => {
        this.saving = false;
      });
    },
    removeDocument() {
      if (!this.form.id) return;
      this.$confirm('清除后公开文档将不再展示当前接口的说明、注意事项和请求响应示例，是否继续？', '清除接口文档', {
        type: 'warning'
      }).then(() => this.$request.delete(`/api/open-api-doc/endpoint/${this.form.id}`)).then(response => {
        if (!response.data.result) {
          this.$message.error(response.data.message || '清除文档失败');
          return;
        }
        const target = this.endpoints.find(item => item.operationId === this.form.operationId);
        if (target) Object.assign(target, { id: null, description: '', notes: '', requestExamples: [], responseExamples: [], updateTime: '', configured: false });
        this.form = { id: null, operationId: this.form.operationId, description: '', notes: '', requestExamples: [], responseExamples: [] };
        this.editorSnapshot = this.documentSnapshot();
        this.editorVisible = false;
        this.$message.success('接口文档已清除');
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(this.errorMessage(error, '清除文档失败'));
        }
      });
    },
    openPortal() {
      const route = this.$router.resolve({ name: 'openPlatformIntroduction' });
      window.open(route.href, '_blank', 'noopener');
    },
    openEndpoint(row) {
      const route = this.$router.resolve({ name: 'openPlatformApiDetail', params: { operationId: row.operationId } });
      window.open(route.href, '_blank', 'noopener');
    },
    formatTime(value) {
      return value ? String(value).replace('T', ' ').replace(/\.\d+.*$/, '') : '-';
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : (error && error.message) || fallback;
    }
  }
};
</script>

<style scoped>
.open-api-doc-page { width: 100%; height: 100%; min-height: 0; padding: 24px; color: var(--solar-text-strong, #dff5ff); background: linear-gradient(135deg, rgba(var(--solar-primary-rgb, 0, 153, 255), .08), transparent 36%), var(--solar-page, #001a33); box-sizing: border-box; overflow: hidden; }
.doc-panel { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0; padding: 22px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .24); border-radius: 10px; background: rgba(var(--solar-page-rgb, 0, 25, 49), .78); box-shadow: 0 18px 42px rgba(0, 8, 22, .28); box-sizing: border-box; }
.page-heading { display: flex; align-items: center; justify-content: space-between; gap: 24px; flex: 0 0 auto; }
.eyebrow { color: var(--solar-primary, #29d8ff); font-size: 11px; font-weight: 700; letter-spacing: 2px; }
.page-heading h1 { margin: 5px 0 4px; color: var(--solar-text-strong, #f1fbff); font-size: 25px; }
.page-heading p { margin: 0; color: var(--solar-text, #87bad0); font-size: 13px; }
.heading-actions { display: flex; flex: 0 0 auto; gap: 8px; }
.summary-grid { display: grid; grid-template-columns: repeat(3, minmax(150px, 1fr)); gap: 12px; margin: 18px 0 14px; }
.summary-card { display: flex; align-items: center; justify-content: space-between; padding: 13px 17px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .22); border-radius: 7px; background: rgba(var(--solar-raised-rgb, 0, 52, 88), .42); }
.summary-card span { color: var(--solar-text, #8bc9df); font-size: 12px; }
.summary-card strong { color: var(--solar-text-strong, #eafaff); font-family: Consolas, monospace; font-size: 22px; }
.summary-card.configured strong { color: var(--solar-text, #49d7a5); }
.summary-card.pending strong { color: var(--solar-warning, #f3bf63); }
.filter-panel { display: grid; grid-template-columns: minmax(280px, 1fr) 150px 170px; gap: 10px; margin-bottom: 14px; }
.table-panel { flex: 1 1 auto; min-height: 0; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .28); border-radius: 8px; overflow: hidden; background: rgba(var(--solar-page-rgb, 0, 18, 36), .42); box-shadow: 0 10px 22px rgba(0, 10, 24, .18); }
.method-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 54px; height: 23px; border-radius: 4px; font-family: Consolas, monospace; font-size: 11px; font-weight: 800; }
.method-badge.get { color: var(--solar-text, #62ddb3); background: rgba(42, 177, 129, .15); }
.method-badge.post { color: var(--solar-warning, #ffd079); background: rgba(214, 147, 34, .16); }
.method-badge.put, .method-badge.patch { color: var(--solar-text, #7bc9ff); background: rgba(var(--solar-primary-rgb, 45, 142, 214), .17); }
.method-badge.delete { color: var(--solar-danger, #ff8f9b); background: rgba(211, 72, 87, .16); }
.path-code { color: var(--solar-text-strong, #a9ddf2); font-family: Consolas, monospace; font-size: 12px; }
.doc-actions { display: flex; align-items: center; justify-content: center; gap: 8px; white-space: nowrap; }
.open-api-doc-page /deep/ .doc-actions .el-button--text { min-width: 0; margin: 0 !important; padding: 5px 8px !important; border: 1px solid rgba(var(--solar-border-strong-rgb, 55, 174, 230), .42); border-radius: 4px; background: rgba(var(--solar-surface-rgb, 2, 46, 78), .72); color: var(--solar-text, #83d9ff); line-height: 18px; }
.open-api-doc-page /deep/ .doc-actions .el-button--text:hover, .open-api-doc-page /deep/ .doc-actions .el-button--text:focus { border-color: var(--solar-border-strong, #2bc8ff); background: rgba(var(--solar-raised-rgb, 0, 83, 126), .82); color: var(--solar-text-strong, #fff); }
.open-api-doc-page /deep/ .doc-actions .el-button--text.is-disabled { border-color: rgba(91, 133, 151, .2); background: rgba(31, 55, 68, .3); color: #57798a; }
.secondary-button { color: var(--solar-text-strong, #bcecff); border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .42); background: rgba(var(--solar-raised-rgb, 0, 63, 102), .34); }
.editor-dialog-title { display: flex; align-items: center; gap: 12px; }
.dialog-title-icon { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border: 1px solid rgba(70, 205, 255, .32); border-radius: 9px; color: var(--solar-text, #62ddff); background: linear-gradient(145deg, rgba(var(--solar-primary-rgb, 16, 157, 211), .24), rgba(var(--solar-raised-rgb, 0, 69, 112), .3)); box-shadow: inset 0 1px 0 rgba(255, 255, 255, .08); }
.editor-dialog-title > div { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.editor-dialog-title strong { color: var(--solar-text-strong, #f1fbff); font-size: 17px; line-height: 21px; }
.editor-dialog-title small { color: var(--solar-muted, #76aabd); font-size: 11px; font-weight: 400; line-height: 15px; }
.endpoint-identity { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px 20px; align-items: center; flex: 0 0 auto; padding: 14px 18px 14px 20px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .2); border-radius: 9px; background: linear-gradient(105deg, rgba(var(--solar-raised-rgb, 0, 71, 111), .46), rgba(var(--solar-surface-rgb, 0, 38, 69), .34)); overflow: hidden; }
.endpoint-identity::before { position: absolute; top: 10px; bottom: 10px; left: 0; width: 3px; border-radius: 0 4px 4px 0; background: var(--solar-primary, #2bc8ff); box-shadow: 0 0 14px rgba(var(--solar-primary-rgb, 43, 200, 255), .55); content: ''; }
.identity-copy { display: flex; min-width: 0; align-items: center; }
.identity-copy strong { color: var(--solar-text-strong, #effcff); font-size: 15px; line-height: 22px; }
.identity-route { gap: 11px; }
.identity-route { display: flex; min-width: 0; align-items: center; }
.identity-route code { color: var(--solar-text-strong, #c7efff); font-family: Consolas, monospace; font-size: 13px; word-break: break-all; }
.editor-size-button { grid-column: 2; grid-row: 1 / 3; align-self: center; padding: 8px 11px !important; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .28) !important; border-radius: 6px; color: var(--solar-text, #8cddf9); background: rgba(var(--solar-raised-rgb, 0, 61, 96), .32); }
.editor-size-button:hover { border-color: var(--solar-border-strong, #2bc8ff) !important; color: var(--solar-text-strong, #fff); background: rgba(0, 91, 132, .58); }
.editor-workspace { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(380px, .92fr); gap: 16px; min-height: 0; margin-top: 16px; overflow: hidden; flex: 1 1 auto; }
.editor-column { min-width: 0; min-height: 0; padding-right: 11px; overflow-x: hidden; overflow-y: auto; scrollbar-color: rgba(var(--solar-muted-rgb, 67, 181, 221), .55) rgba(0, 25, 47, .48); scrollbar-gutter: stable; scrollbar-width: thin; }
.editor-column::-webkit-scrollbar, .live-preview::-webkit-scrollbar { width: 8px; }
.editor-column::-webkit-scrollbar-track, .live-preview::-webkit-scrollbar-track { border-radius: 8px; background: rgba(var(--solar-page-rgb, 0, 25, 47), .48); }
.editor-column::-webkit-scrollbar-thumb, .live-preview::-webkit-scrollbar-thumb { border: 2px solid rgba(var(--solar-border-rgb, 0, 25, 47), .48); border-radius: 8px; background: rgba(67, 181, 221, .65); }
.editor-column::-webkit-scrollbar-thumb:hover, .live-preview::-webkit-scrollbar-thumb:hover { background: rgba(70, 205, 244, .85); }
.editor-section { padding: 16px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .18); border-radius: 10px; background: linear-gradient(145deg, rgba(var(--solar-surface-rgb, 0, 46, 76), .38), rgba(var(--solar-page-rgb, 0, 24, 46), .42)); box-shadow: inset 0 1px 0 rgba(163, 227, 255, .03); }
.field-heading { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 14px; }
.field-heading > div { display: flex; align-items: center; gap: 9px; }
.field-heading strong { color: var(--solar-text-strong, #dcf7ff); font-size: 13px; }
.field-heading small { color: var(--solar-muted, #6f9caf); font-size: 10px; font-weight: 400; }
.field-order { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 20px; border: 1px solid rgba(var(--solar-border-strong-rgb, 51, 195, 242), .24); border-radius: 5px; color: var(--solar-text, #4bd4ff); background: rgba(var(--solar-raised-rgb, 0, 87, 126), .28); font-family: Consolas, monospace; font-size: 10px; }
.markdown-editor-shell { border-radius: 7px; box-shadow: 0 8px 20px rgba(0, 8, 20, .14); }
.markdown-toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding: 7px 8px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .24); border-bottom: 0; border-radius: 6px 6px 0 0; background: rgba(var(--solar-page-rgb, 0, 35, 62), .88); }
.format-actions { display: flex; flex: 1; flex-wrap: wrap; gap: 4px; min-width: 0; }
.markdown-help { display: flex; align-items: flex-start; gap: 7px; margin: 2px 4px 4px; padding: 9px 11px; border: 1px dashed rgba(var(--solar-border-strong-rgb, 63, 184, 255), .17); border-radius: 7px; color: var(--solar-muted, #73a9bd); background: rgba(var(--solar-surface-rgb, 0, 49, 76), .22); font-size: 11px; line-height: 20px; }
.markdown-help i { color: var(--solar-primary, #36c9f5); }
.example-editor-list { display: flex; flex-direction: column; gap: 12px; }
.example-editor-card { display: flex; flex-direction: column; gap: 9px; padding: 13px; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .2); border-radius: 7px; background: rgba(var(--solar-page-rgb, 0, 25, 47), .54); }
.example-card-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.example-card-heading strong { color: var(--solar-text-strong, #cdeffc); font-size: 12px; }
.example-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.example-editor-empty { padding: 20px; color: var(--solar-muted, #6e9cb0); border: 1px dashed rgba(83, 159, 190, .25); border-radius: 6px; background: rgba(var(--solar-surface-rgb, 0, 44, 70), .18); font-size: 12px; text-align: center; }
.add-example-button { color: var(--solar-text, #8cddf9); border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .32); background: rgba(var(--solar-raised-rgb, 0, 61, 96), .38); }
.example-preview-list { display: flex; flex-direction: column; gap: 12px; }
.example-preview-card { border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .18); border-radius: 7px; background: rgba(var(--solar-page-rgb, 0, 20, 38), .5); overflow: hidden; }
.example-preview-card > div { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .14); }
.example-preview-card strong { color: var(--solar-text-strong, #dff7ff); font-size: 12px; }
.example-preview-card > div code { padding: 2px 6px; color: var(--solar-text, #69daf8); border-radius: 4px; background: rgba(0, 93, 128, .3); font-size: 10px; }
.example-preview-card p { margin: 0; padding: 9px 12px; color: var(--solar-muted, #82b5c7); border-top: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .14); font-size: 11px; line-height: 1.6; }
.example-preview-card pre { margin: 0; padding: 12px; overflow: auto; color: var(--solar-text-strong, #c9eaf5); background: var(--solar-page, #001426); font-family: Consolas, monospace; font-size: 11px; line-height: 1.65; white-space: pre-wrap; word-break: break-word; }
.live-preview { min-width: 0; min-height: 0; overflow-x: hidden; overflow-y: auto; scrollbar-color: rgba(var(--solar-muted-rgb, 67, 181, 221), .55) rgba(0, 25, 47, .48); scrollbar-gutter: auto; scrollbar-width: thin; }
.live-preview.has-scrollbar { padding-right: 11px; }
.live-preview-panel { min-height: 100%; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .24); border-radius: 10px; background: linear-gradient(155deg, rgba(var(--solar-page-rgb, 0, 33, 59), .84), rgba(var(--solar-page-rgb, 0, 17, 34), .78)); box-shadow: inset 0 1px 0 rgba(162, 228, 255, .04); overflow: hidden; box-sizing: border-box; }
.preview-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 13px 16px; border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .18); background: linear-gradient(90deg, rgba(var(--solar-raised-rgb, 0, 67, 104), .64), rgba(var(--solar-surface-rgb, 0, 42, 72), .48)); }
.preview-heading > div { display: flex; align-items: center; gap: 8px; color: var(--solar-text-strong, #e7faff); font-size: 13px; }
.preview-heading small { color: var(--solar-muted, #6fa6bb); font-size: 10px; }
.live-dot { width: 7px; height: 7px; border-radius: 50%; background: #42dda7; box-shadow: 0 0 0 4px rgba(66, 221, 167, .12); }
.preview-scroll { min-height: 0; padding: 20px; }
.preview-scroll section + section { margin-top: 24px; padding-top: 22px; border-top: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .15); }
.preview-scroll h3 { margin: 0 0 12px; color: var(--solar-text-strong, #effcff); font-size: 15px; }
.empty-preview { margin: 0; padding: 18px; color: var(--solar-muted, #6e9cb0); border: 1px dashed rgba(83, 159, 190, .25); border-radius: 6px; background: rgba(var(--solar-surface-rgb, 0, 44, 70), .18); font-size: 12px; text-align: center; }
.markdown-body { color: var(--solar-text-strong, #b8dbea); font-size: 13px; line-height: 1.8; overflow-wrap: anywhere; }
.markdown-body /deep/ p { margin: 0 0 12px; }
.markdown-body /deep/ h1, .markdown-body /deep/ h2, .markdown-body /deep/ h3, .markdown-body /deep/ h4 { margin: 18px 0 9px; color: var(--solar-text-strong, #ecfbff); }
.markdown-body /deep/ h1:first-child, .markdown-body /deep/ h2:first-child, .markdown-body /deep/ h3:first-child, .markdown-body /deep/ h4:first-child { margin-top: 0; }
.markdown-body /deep/ ul, .markdown-body /deep/ ol { margin: 8px 0; padding-left: 24px; }
.markdown-body /deep/ li + li { margin-top: 3px; }
.markdown-body /deep/ code { padding: 2px 5px; border-radius: 3px; color: var(--solar-warning, #ffd079); background: rgba(0, 10, 22, .58); }
.markdown-body /deep/ pre { padding: 12px; overflow: auto; border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .16); border-radius: 5px; background: var(--solar-page, #001426); }
.markdown-body /deep/ pre code { padding: 0; color: var(--solar-text-strong, #bdeeff); background: transparent; }
.markdown-body /deep/ blockquote { margin: 12px 0; padding: 8px 12px; border-left: 3px solid var(--solar-border-strong, #25c9f2); background: rgba(var(--solar-raised-rgb, 0, 82, 116), .2); color: var(--solar-text, #9ed5e7); }
.markdown-body /deep/ blockquote p:last-child { margin-bottom: 0; }
.markdown-body /deep/ a { color: var(--solar-text, #56d8ff); text-decoration: none; }
.markdown-body /deep/ a:hover { text-decoration: underline; }
.markdown-body /deep/ table { width: 100%; margin: 12px 0; border-collapse: collapse; font-size: 12px; }
.markdown-body /deep/ th, .markdown-body /deep/ td { padding: 8px 10px; border: 1px solid rgba(79, 177, 217, .3); text-align: left; }
.markdown-body /deep/ th { color: var(--solar-text-strong, #e9faff); background: rgba(var(--solar-raised-rgb, 0, 76, 113), .42); }
.markdown-body /deep/ tr:nth-child(even) { background: rgba(var(--solar-raised-rgb, 0, 60, 92), .16); }
.markdown-body /deep/ img { display: block; max-width: 100%; height: auto; margin: 12px 0; border-radius: 6px; }
.markdown-body /deep/ del { color: var(--solar-muted, #769daf); }
.markdown-body /deep/ hr { margin: 18px 0; border: 0; border-top: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .25); }
.markdown-body /deep/ .task-list-item { list-style: none; }
.markdown-body /deep/ .task-list-item-checkbox { margin: 0 7px 0 -20px; accent-color: var(--solar-primary, #26c9f2); }
.markdown-body.notes-body { color: var(--solar-text-strong, #c8d8df); }
.dialog-footer { display: flex; align-items: center; width: 100%; }
.footer-spacer { flex: 1; }
.save-shortcut { margin-right: 13px; color: var(--solar-muted, #6f9fb2); font-size: 10px; }
.delete-button { color: var(--solar-danger, #ff8491); }
.open-api-doc-page /deep/ .el-input__inner, .open-api-doc-page /deep/ .el-textarea__inner { color: var(--solar-text-strong, #d9f5ff); border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .28); background: rgba(var(--solar-page-rgb, 0, 20, 39), .62); }
.open-api-doc-page /deep/ .doc-table.el-table,
.open-api-doc-page /deep/ .doc-table .el-table__header-wrapper,
.open-api-doc-page /deep/ .doc-table .el-table__body-wrapper,
.open-api-doc-page /deep/ .doc-table .el-table__fixed,
.open-api-doc-page /deep/ .doc-table .el-table__fixed-right { color: var(--solar-text-strong, #dff5ff); background: transparent; }
.open-api-doc-page /deep/ .doc-table.el-table--border { border: 0 !important; border-radius: 0; }
.open-api-doc-page /deep/ .doc-table.el-table::before,
.open-api-doc-page /deep/ .doc-table.el-table::after,
.open-api-doc-page /deep/ .doc-table .el-table__fixed::before,
.open-api-doc-page /deep/ .doc-table .el-table__fixed-right::before { display: none !important; }
.open-api-doc-page /deep/ .doc-table th,
.open-api-doc-page /deep/ .doc-table .el-table__fixed-right-patch { color: var(--solar-text-strong, #bcecff); border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), .24) !important; background: rgba(var(--solar-page-rgb, 0, 24, 48), .96) !important; }
.open-api-doc-page /deep/ .doc-table td { color: var(--solar-text-strong, #d7f4ff); border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), .18) !important; background: rgba(var(--solar-surface-rgb, 0, 36, 66), .88) !important; }
.open-api-doc-page /deep/ .doc-table .el-table__body tr:hover > td,
.open-api-doc-page /deep/ .doc-table .el-table__body tr.hover-row > td { background: rgba(var(--solar-raised-rgb, 0, 65, 100), .98) !important; }
.open-api-doc-page /deep/ .doc-table .el-table__empty-block { background: rgba(var(--solar-page-rgb, 0, 31, 58), .9); }
.open-api-doc-page /deep/ .doc-table .el-table__empty-text { color: var(--solar-muted, #79b8d3); }
.open-api-doc-page /deep/ .doc-table .el-tag--success { color: var(--solar-text, #73e9b1); border-color: rgba(62, 213, 142, .42); background: rgba(25, 132, 88, .2); }
.open-api-doc-page /deep/ .doc-table .el-tag--info { color: var(--solar-text, #a9c6d5); border-color: rgba(132, 166, 184, .36); background: rgba(72, 99, 116, .22); }
.open-api-doc-page /deep/ .doc-table .el-loading-mask { background: rgba(var(--solar-page-rgb, 0, 22, 43), .8); }
@media (max-width: 1000px) { .page-heading { align-items: flex-start; flex-direction: column; } .filter-panel { grid-template-columns: 1fr 140px; } .filter-panel > :first-child { grid-column: 1 / -1; } }
@media (max-width: 680px) { .example-meta-grid { grid-template-columns: 1fr; } }
</style>

<style>
.open-api-doc-dialog.el-dialog {
  display: flex;
  height: 90vh;
  max-width: calc(100vw - 40px);
  max-height: 920px;
  margin-top: 5vh !important;
  margin-bottom: 0;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .42);
  border-radius: 12px;
  background: radial-gradient(circle at 88% 0%, rgba(var(--solar-primary-rgb, 15, 126, 175), .14), transparent 28%), linear-gradient(180deg, var(--solar-surface, #002844) 0%, var(--solar-page, #00182f) 100%);
  box-shadow: 0 24px 70px rgba(0, 7, 19, .72), inset 0 1px 0 rgba(156, 229, 255, .08);
  overflow: hidden;
  flex-direction: column;
}
.open-api-doc-dialog .el-dialog__header {
  flex: 0 0 auto;
  padding: 15px 24px;
  border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .22);
  background: linear-gradient(90deg, rgba(var(--solar-raised-rgb, 0, 51, 84), .9), rgba(var(--solar-page-rgb, 0, 35, 65), .76));
}
.open-api-doc-dialog .el-dialog__title { color: var(--solar-text-strong, #effbff) !important; font-size: 17px !important; font-weight: 700; }
.open-api-doc-dialog .el-dialog__headerbtn { top: 20px; right: 22px; }
.open-api-doc-dialog .el-dialog__close { color: var(--solar-text, #8edcf7) !important; }
.open-api-doc-dialog .el-dialog__body {
  display: flex;
  min-height: 0;
  max-height: none;
  padding: 18px 24px 16px;
  color: var(--solar-text-strong, #b8dbea);
  background: transparent;
  overflow: hidden;
  flex: 1 1 auto;
  flex-direction: column;
}
.open-api-doc-dialog .el-dialog__footer {
  flex: 0 0 auto;
  padding: 13px 24px 15px;
  border-top: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .2);
  background: rgba(var(--solar-page-rgb, 0, 13, 28), .68);
}
.open-api-doc-dialog .el-tabs__nav-wrap::after { height: 1px; background: rgba(var(--solar-primary-rgb, 63, 184, 255), .2); }
.open-api-doc-dialog .el-tabs__item { color: var(--solar-muted, #79b8d1); }
.open-api-doc-dialog .el-tabs__item:hover,
.open-api-doc-dialog .el-tabs__item.is-active { color: var(--solar-primary, #41d8ff); }
.open-api-doc-dialog .el-tabs__active-bar { background: var(--solar-primary, #21c9f5); }
.open-api-doc-dialog .el-form-item { margin-bottom: 14px; }
.open-api-doc-dialog .el-form-item__label { display: flex; width: 100%; padding: 0 0 11px; color: var(--solar-text-strong, #bcecff) !important; font-weight: 600; line-height: normal; }
.open-api-doc-dialog .el-textarea__inner {
  border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .28);
  border-radius: 0 0 6px 6px;
  background: var(--solar-page, #00182e);
  color: var(--solar-text-strong, #d9f5ff);
  font-family: Consolas, "Microsoft YaHei", monospace;
  font-size: 13px;
  line-height: 1.75;
  resize: vertical;
}
.open-api-doc-dialog .el-textarea__inner:hover,
.open-api-doc-dialog .el-textarea__inner:focus { border-color: var(--solar-border-strong, #28c7ff); }
.open-api-doc-dialog .el-textarea__inner::placeholder { color: #557f93; }
.open-api-doc-dialog .el-input__count,
.open-api-doc-dialog .el-input__count-inner { color: var(--solar-muted, #6296aa) !important; background: var(--solar-page, #00182e) !important; }
.open-api-doc-dialog .el-textarea .el-input__count { right: 10px; bottom: 3px; padding-left: 6px; border-radius: 4px; }
.open-api-doc-dialog .example-editor-card .el-input {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}
.open-api-doc-dialog .example-editor-card .el-input__inner {
  height: 36px;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), .28) !important;
  border-radius: 6px !important;
  outline: 0;
  background: var(--solar-page, #00182e) !important;
  box-shadow: none !important;
  color: var(--solar-text-strong, #d9f5ff) !important;
}
.open-api-doc-dialog .example-editor-card .el-input__inner:hover,
.open-api-doc-dialog .example-editor-card .el-input__inner:focus {
  border-color: var(--solar-border-strong, #28c7ff) !important;
  box-shadow: none !important;
}
.open-api-doc-dialog .example-card-heading .el-button {
  min-width: 68px;
  height: 32px;
  margin: 0;
  padding: 7px 14px !important;
  border: 1px solid rgba(255, 101, 116, .36);
  border-radius: 5px;
  background: rgba(126, 34, 48, .2);
  color: var(--solar-danger, #ff929d);
  line-height: 16px;
  box-sizing: border-box;
}
.open-api-doc-dialog .example-card-heading .el-button:hover,
.open-api-doc-dialog .example-card-heading .el-button:focus {
  border-color: rgba(255, 132, 145, .75);
  background: rgba(151, 43, 58, .34);
  color: #ffc1c7;
}
.open-api-doc-dialog .markdown-toolbar .el-button {
  min-width: 34px;
  padding: 6px 8px;
  border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .18);
  background: rgba(var(--solar-raised-rgb, 0, 50, 82), .62);
  color: var(--solar-text-strong, #a8e6f8);
}
.open-api-doc-dialog .markdown-toolbar .el-button:hover,
.open-api-doc-dialog .markdown-toolbar .el-button:focus {
  z-index: 2;
  border-color: var(--solar-border-strong, #2bc8ff);
  background: rgba(0, 91, 132, .86);
  color: var(--solar-text-strong, #fff);
}
.open-api-doc-dialog .format-actions .el-button + .el-button { margin-left: 0; }
.open-api-doc-dialog .format-actions .el-button:nth-child(2),
.open-api-doc-dialog .format-actions .el-button:nth-child(6),
.open-api-doc-dialog .format-actions .el-button:nth-child(10),
.open-api-doc-dialog .format-actions .el-button:nth-child(13) { margin-left: 5px; }
.open-api-doc-dialog .markdown-toolbar .template-button { min-width: 108px; margin-left: 0; }
.open-api-doc-dialog.is-fullscreen { display: flex; width: 100%; height: 100vh; max-width: none; max-height: none; margin: 0 !important; border-radius: 0; box-sizing: border-box; flex-direction: column; }
.open-api-doc-dialog.is-fullscreen .el-dialog__body { display: flex; min-height: 0; max-height: none; flex: 1; flex-direction: column; }
.open-api-doc-dialog.is-fullscreen .editor-workspace { min-height: 0; max-height: none; flex: 1; }
.open-api-doc-dialog .secondary-button {
  border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .38);
  background: rgba(var(--solar-raised-rgb, 0, 55, 90), .7);
  color: var(--solar-text-strong, #bcecff);
}
.open-api-doc-dialog .secondary-button:hover { border-color: var(--solar-border-strong, #2bc8ff); background: rgba(var(--solar-raised-rgb, 0, 82, 120), .86); color: var(--solar-text-strong, #fff); }
.open-api-doc-dialog .delete-button { padding: 8px 12px; border: 1px solid rgba(255, 101, 116, .34); border-radius: 5px; background: rgba(126, 34, 48, .18); color: var(--solar-danger, #ff929d); }
.open-api-doc-dialog .el-button--primary { color: var(--solar-text-strong, #fff) !important; }
.open-api-doc-select.el-select-dropdown { border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), .34); background: var(--solar-surface, #002742); }
.open-api-doc-select .el-select-dropdown__item { color: var(--solar-text-strong, #aee2f4); }
.open-api-doc-select .el-select-dropdown__item.hover,
.open-api-doc-select .el-select-dropdown__item:hover { background: var(--solar-raised, #00416a); color: var(--solar-text-strong, #fff); }
.open-api-doc-select .popper__arrow::after { border-bottom-color: var(--solar-border, #002742) !important; }
@media (max-width: 760px) {
  .open-api-doc-dialog.el-dialog { height: 94vh; max-width: calc(100vw - 20px); margin-top: 3vh !important; }
  .open-api-doc-dialog .el-dialog__header { padding: 13px 16px; }
  .open-api-doc-dialog .el-dialog__body { display: block; padding: 14px 16px; overflow-y: auto; }
  .open-api-doc-dialog .el-dialog__footer { padding: 12px 16px; }
  .open-api-doc-dialog .editor-workspace { display: block; margin-top: 12px; overflow: visible; }
  .open-api-doc-dialog .editor-column { padding-right: 0; overflow: visible; }
  .open-api-doc-dialog .live-preview { min-height: 420px; margin-top: 14px; }
  .open-api-doc-dialog .markdown-toolbar { align-items: flex-start; flex-direction: column; }
  .open-api-doc-dialog .markdown-toolbar .template-button { width: 100%; }
  .open-api-doc-dialog .endpoint-identity { display: block; padding: 13px 14px 13px 17px; }
  .open-api-doc-dialog .identity-route { padding-top: 9px; }
  .open-api-doc-dialog .editor-size-button { display: none; }
  .open-api-doc-dialog .save-shortcut { display: none; }
}
</style>
